import { useCallback, useEffect, useRef, useState } from 'react';
import { reportErrorMessage, ReportErrorCopy } from './utils';

export type ReportState<T> =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'success'; data: T | undefined };

/**
 * Runs one GIL report request and re-runs it whenever `deps` change.
 * `fetcher` returns undefined when the ids it needs are missing, which is
 * reported as an invalid link rather than a server failure.
 */
export const useGilReport = <T>(
  fetcher: () => Promise<T | undefined> | undefined,
  deps: unknown[],
  errorCopy?: ReportErrorCopy
) => {
  const [state, setState] = useState<ReportState<T>>({ status: 'loading' });
  //~ A later request for different ids must win over an earlier one still in flight.
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    const request = fetcher();
    if (!request) {
      setState({ status: 'error', message: 'Invalid report link' });
      return;
    }
    setState({ status: 'loading' });
    try {
      const data = await request;
      if (id === requestId.current) {
        setState({ status: 'success', data });
      }
    } catch (error) {
      console.log(error);
      if (id === requestId.current) {
        setState({
          status: 'error',
          message: reportErrorMessage(error, errorCopy),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    load();
  }, [load]);

  return { state, reload: load };
};
